import { getDownloadURL, ref } from 'firebase/storage';
import type { ProposalNodeSet, SupplierShipToAddress } from '../types';
import { getRfqHubFileRelease } from './rfqHubFileReleaseService';
import { resolveTimberCsvTrueDimensions } from './rfqHubTimberImport';
import { storage } from './firebase';
import {
  buildTimberSupplierRequestHtml,
  DEFAULT_TIMBER_RFQ_DRYNESS_SPEC,
  downloadTimberSupplierCsv,
  prepareTimberSupplierCsvText,
  TIMBER_SUPPLIER_POPUP_BLOCKED_MSG,
  type TimberSpeciesDrynessEntry,
  type TimberSupplierExportOptions,
} from './supplierTimberExport';
import type { StructureSpeciesRule } from './timberRfqStructureSpecies';

export type RfqPublishTimberExportInput = {
  rfqProjectId?: string;
  hubFileReleaseId?: string;
  dealTitle?: string;
  label?: string;
  sourceFileName?: string;
  nodeSets?: ProposalNodeSet[];
  requestedSpecies?: string[];
  speciesDryness?: TimberSpeciesDrynessEntry[];
  /** Species narrowed per structure (invite `structureSpecies`). */
  structureSpecies?: StructureSpeciesRule[];
  shipToAddress?: SupplierShipToAddress;
};

function timberExportOptions(input: RfqPublishTimberExportInput): TimberSupplierExportOptions | undefined {
  let requestedSpeciesEntries = input.speciesDryness?.length ? input.speciesDryness : undefined;
  if (!requestedSpeciesEntries?.length && input.requestedSpecies?.length) {
    requestedSpeciesEntries = input.requestedSpecies.map(species => ({
      species,
      drynessSpec: DEFAULT_TIMBER_RFQ_DRYNESS_SPEC,
    }));
  }
  const shipToAddress = input.shipToAddress?.line1?.trim() ? input.shipToAddress : undefined;
  if (!requestedSpeciesEntries && !shipToAddress) return undefined;
  return {
    ...(requestedSpeciesEntries ? { requestedSpeciesEntries } : {}),
    ...(requestedSpeciesEntries && input.structureSpecies?.length ? { structureSpecies: input.structureSpecies } : {}),
    ...(shipToAddress ? { shipToAddress } : {}),
  };
}

/** HTML for the timber RFQ PDF suppliers receive (species/dryness from RFQ invite). */
export async function buildRfqPublishTimberPdfHtml(input: RfqPublishTimberExportInput): Promise<string> {
  if (!input.nodeSets?.length) {
    throw new Error('No timber data available for PDF preview.');
  }
  return buildTimberSupplierRequestHtml(
    { nodeSets: input.nodeSets },
    input.dealTitle,
    new Date(),
    undefined,
    timberExportOptions(input),
  );
}

/** Open timber RFQ PDF HTML in a new tab (print-ready). */
export function openRfqPublishTimberPdfPreview(html: string): void {
  const printWindow = window.open('about:blank', '_blank');
  if (!printWindow) {
    alert(TIMBER_SUPPLIER_POPUP_BLOCKED_MSG);
    return;
  }
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
}

function triggerTextDownload(text: string, filename: string): void {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function sanitizeFilenameBase(raw: string): string {
  return raw.replace(/[^\w\-]+/g, '_').replace(/^_+|_+$/g, '') || 'timber-list';
}

/** Download the CSV that will be attached to timber RFQ publish emails. */
export async function downloadRfqPublishTimberCsv(input: RfqPublishTimberExportInput): Promise<void> {
  const rfqProjectId = (input.rfqProjectId || '').trim();
  const hubFileReleaseId = (input.hubFileReleaseId || '').trim();

  if (input.nodeSets?.length) {
    const base = sanitizeFilenameBase(
      (input.sourceFileName || input.label || input.dealTitle || 'timber-list').replace(/\.csv$/i, ''),
    );
    downloadTimberSupplierCsv(
      { nodeSets: input.nodeSets },
      base,
      timberExportOptions(input),
    );
    return;
  }

  if (rfqProjectId && hubFileReleaseId) {
    const release = await getRfqHubFileRelease(rfqProjectId, hubFileReleaseId);
    if (release?.storagePath) {
      const fileName = release.fileName || input.sourceFileName || 'timber-list.csv';
      const url = release.releasedUrl?.trim() || (await getDownloadURL(ref(storage, release.storagePath)));
      const text = await fetch(url).then((r) => {
        if (!r.ok) throw new Error('Could not download staged hub CSV.');
        return r.text();
      });
      if (text.trim()) {
        const trueDimensions = await resolveTimberCsvTrueDimensions(release.sourceFileId, release.trueDimensions);
        triggerTextDownload(
          prepareTimberSupplierCsvText(text, input.requestedSpecies ?? [], { trueDimensions }),
          fileName,
        );
        return;
      }
    }
  }

  throw new Error('No timber data available for CSV download.');
}
